/*
 * fn: observeMediavineAdhesion
 *
 * Watches the MediaVine adhesion elements and sets a css variable to their height for components to use
 *
 */
import {
  ADHESION_DESKTOP_CSS_VAR,
  ADHESION_DESKTOP_WRAPPER_ID,
  ADHESION_EVENT,
  ADHESION_MOBILE_CSS_VAR,
  ADHESION_MOBILE_WRAPPER_ID,
} from '@/defs/ad'

const ADHESIONS = [
  { cssVar: ADHESION_DESKTOP_CSS_VAR, wrapperId: ADHESION_DESKTOP_WRAPPER_ID },
  { cssVar: ADHESION_MOBILE_CSS_VAR, wrapperId: ADHESION_MOBILE_WRAPPER_ID },
]

export const observeMediavineAdhesion = () => {
  const root = window.document.documentElement

  const dispatchAdhesionEvent = () => window.dispatchEvent(new Event(ADHESION_EVENT))

  const detectors = ADHESIONS.map(({ cssVar, wrapperId }) => {
    let watched: HTMLElement | null = null
    let resizeObserver: ResizeObserver | null = null
    let heightLast: number | null = null

    const setHeight = (height: number) => {
      if (height === heightLast) return

      heightLast = height
      root.style.setProperty(cssVar, `${height}px`)
      dispatchAdhesionEvent()
    }

    const detectWrapper = () => {
      const wrapper = window.document.getElementById(wrapperId)
      if (wrapper === watched) return

      resizeObserver?.disconnect()
      resizeObserver = null
      watched = wrapper

      if (!wrapper) {
        setHeight(0)
        return
      }

      setHeight(wrapper.offsetHeight)
      resizeObserver = new ResizeObserver(() => watched && setHeight(watched.offsetHeight))
      resizeObserver.observe(wrapper)
    }

    const cleanup = () => {
      resizeObserver?.disconnect()
      resizeObserver = null
      watched = null
      heightLast = null
      root.style.removeProperty(cssVar)
    }

    return { cleanup, detectWrapper }
  })

  const detectWrappers = () => detectors.forEach((detector) => detector.detectWrapper())

  detectWrappers()

  const mutationObserver = new MutationObserver(detectWrappers)
  mutationObserver.observe(window.document.body, { childList: true, subtree: true })

  return () => {
    mutationObserver.disconnect()
    detectors.forEach((detector) => detector.cleanup())
    dispatchAdhesionEvent()
  }
}
